/**
 * What a hit does: to the ranger (stagger, body-armour wound roll, downed), to a soldier
 * (knock-down, incapacitated or killed - and what that does to the men who see it), and to
 * a prop (destruction, cook-off chains). Explosions live here too.
 */

import { bark, makeNoise } from './alert';
import { type BlastPower, blastVerdict, mayReach } from './breach';
import { type Ctx, isAlive, isFighter } from './ctx';
import { EYE } from './enemies/perception';
import { blastHazards } from './hazards/blast';
import { angleDiff, atan2, PI } from './math';
import { PROPS } from './props';
import { refuseBlast, refuseWall } from './refusal';
import { clearSight } from './smoke';
import { BOMB, ENEMY, GRENADE, RANGER, secs, TACTICS } from './tuning';
import type { Enemy, HurtCause } from './types';
import { isDog } from './enemies/specials/kinds';

export type { BlastPower } from './breach';
/* The refusal prefix moved to refusal.ts; the HUD, the sound bank and the projectiles still read it from here. */
export { isRefusal, REFUSED } from './refusal';

export function endMission(ctx: Ctx, status: 'kia' | 'captured', cause?: HurtCause): void {
  ctx.world.ranger.status = status;
  if (cause) ctx.world.ranger.hurtBy = cause;
}

/** A bullet or fragment has struck the ranger: stagger, then the body-armour wound roll (02 §5). */
export function hitRanger(ctx: Ctx, heavy: boolean): void {
  const r = ctx.world.ranger;
  if (r.status !== 'active' && r.status !== 'downed' && r.status !== 'boarding') return;
  if (r.status === 'downed') {
    ctx.events.push({ type: 'rangerHit', wounded: true });
    endMission(ctx, 'kia', 'gunfire');
    return;
  }
  r.staggerTicks = RANGER.staggerTicks;
  r.action = 'none';
  r.actionTicks = 0;
  const base = heavy ? RANGER.emplacementWoundChance : RANGER.woundChance;
  const chance = Math.min(RANGER.woundChanceCap, base + RANGER.woundChancePerRecentHit * r.recentHits.length);
  r.recentHits.push(0);
  const wounded = ctx.rng.chance(chance);
  ctx.events.push({ type: 'rangerHit', wounded });
  if (wounded) woundRanger(ctx, 1);
}

export function woundRanger(ctx: Ctx, count: number, cause: HurtCause = 'gunfire'): void {
  const r = ctx.world.ranger;
  const mods = ctx.world.mods;
  if (r.status === 'downed') {
    endMission(ctx, 'kia', cause);
    return;
  }
  if (r.status !== 'active' && r.status !== 'boarding') return;
  // Lucky (04 §4): once an operation, the wound that would have put him down goes through his sleeve.
  if (r.wounds + count >= 3 && mods.luckyCharges > 0) {
    mods.luckyCharges--;
    ctx.events.push({ type: 'pickup', what: 'LUCKY - IT MISSED' });
    return;
  }
  r.hurtBy = cause;
  r.wounds = Math.min(3, r.wounds + count);
  r.action = 'none';
  r.actionTicks = 0;
  if (r.wounds >= 3) {
    // Operation rules: the third wound puts the ranger down instead of killing outright (04 §3).
    r.status = 'downed';
    r.stance = 'crawl';
    r.running = false;
    r.bleedTicks = Math.round(RANGER.bleedOutTicks * mods.bleedOutTime);
    r.weapon = 'carbine';
    ctx.events.push({ type: 'rangerDowned' });
  }
}

/**
 * Does this man see the casualty go down? In his cone and in the open. ROUND7: every man within the shock
 * radius used to react whichever way he faced, so a silent knife kill behind a sentry's back still had the
 * man beside him call "man down" - and that call sends everyone near HIM to look. A kill nobody saw is found
 * later, by the body (perception.ts), which is the design's rule (02 §6.2).
 */
function sees(ctx: Ctx, o: Enemy, victim: Enemy): boolean {
  const off = Math.abs(angleDiff(o.facing, atan2(victim.x - o.x, victim.z - o.z)));
  if (off > (ENEMY.fovDeg / 2) * (PI / 180)) return false;
  return clearSight(ctx, o.x, o.z, EYE, victim.x, victim.z, CASUALTY_SEEN_HEIGHT);
}

/** A man falling is seen by his chest: lower than a man standing, higher than one lying down. */
const CASUALTY_SEEN_HEIGHT = 1;

/** What a casualty does to the men who see it happen (02 §9.4). */
export function onCasualty(ctx: Ctx, victim: Enemy): void {
  const w = ctx.world;
  const officerFell = victim.kind === 'officer';
  const reach = officerFell ? TACTICS.officerShockRadius : TACTICS.casualtyShockRadius;
  let steadied = false;
  for (const o of w.enemies) {
    if (o.kind === 'officer' && isFighter(o) && o.id !== victim.id) {
      if ((o.x - victim.x) * (o.x - victim.x) + (o.z - victim.z) * (o.z - victim.z) <= TACTICS.officerAura * TACTICS.officerAura) steadied = true;
    }
  }
  let called = false;
  for (const o of w.enemies) {
    if (o.id === victim.id || !isFighter(o) || o.mans !== null || o.state === 'down') continue;
    if ((o.x - victim.x) * (o.x - victim.x) + (o.z - victim.z) * (o.z - victim.z) > reach * reach) continue;
    if (!sees(ctx, o, victim)) continue;
    if (!called) {
      bark(ctx, o, 'manDown');
      called = true;
    }
    if (o.persona === 'jumpy' && !steadied) {
      // He cracks: runs for his friends, and brings them back with him.
      o.state = 'flee';
      o.stateTicks = TACTICS.fleeTicks;
      o.hasSpot = false;
      o.path = [];
    } else if (o.persona === 'aggressive') {
      o.reactTicks = 0;
      o.suppressTicks = 0;
    } else {
      o.suppressTicks = Math.max(o.suppressTicks, TACTICS.suppressTicks * (officerFell ? 2 : 1));
      o.tacticTicks = 0;
    }
  }
}

export function damageEnemy(ctx: Ctx, e: Enemy, amount: number, byPlayer: boolean): void {
  if (!isAlive(e)) return;
  if (e.state === 'wounded') {
    // Already out of the fight; further harm kills him.
    e.state = 'dead';
    ctx.events.push({ type: 'enemyHit', id: e.id, killed: true });
    return;
  }
  e.hp -= amount;
  const out = e.hp <= 0;
  if (out) {
    // Gunfire and fragments sometimes leave a man alive but finished; a knife or a direct blast does not.
    // ROUND8: a dog is never left wounded - one hit kills it, and the hit says so (no roll drawn for it).
    const survives = amount < 50 && !isDog(e.kind) && ctx.rng.chance(TACTICS.woundedChance);
    ctx.events.push({ type: 'enemyHit', id: e.id, killed: !survives });
    e.state = survives ? 'wounded' : 'dead';
    e.stateTicks = survives ? secs(1.5) : 0;
    e.moving = false;
    e.seesRanger = false;
    e.detection = 0;
    e.hp = 0;
    if (e.carryId !== -1) {
      const carried = ctx.world.enemies.find((o) => o.id === e.carryId);
      if (carried) carried.carriedBy = -1;
      e.carryId = -1;
    }
    // Either way he counts: there is no reason to finish off the wounded (02 §11).
    if (byPlayer) ctx.world.ranger.kills++;
    onCasualty(ctx, e);
    return;
  }
  ctx.events.push({ type: 'enemyHit', id: e.id, killed: false });
  // A non-lethal hit knocks him down for a few seconds; he gets back up (as in 1987).
  e.state = 'down';
  e.moving = false;
  e.stateTicks = ctx.rng.int(ENEMY.downTicksMax - ENEMY.downTicksMin) + ENEMY.downTicksMin;
  e.detection = 1;
  e.targetX = ctx.world.ranger.x;
  e.targetZ = ctx.world.ranger.z;
}

export function destroyProp(ctx: Ctx, id: number): void {
  const state = ctx.world.props.find((p) => p.id === id);
  const def = ctx.statics.def.props.find((p) => p.id === id);
  if (!state || !def || state.destroyed) return;
  state.destroyed = true;
  ctx.staticsDirty = true;
  ctx.events.push({ type: 'propDestroyed', id });
  const spec = PROPS[def.kind];
  if (spec.emplacement) ctx.world.emplacementsDestroyed++;
  // Whoever was manning it goes with it.
  for (const e of ctx.world.enemies) if (e.mans === id) damageEnemy(ctx, e, 99, true);
  if (spec.secondaryBlast !== undefined) {
    ctx.world.pendingBlasts.push({ x: def.x, z: def.z, radius: spec.secondaryBlast, ticks: secs(0.3) + ctx.rng.int(secs(0.9)) });
  }
}

/** A prop leaves the map whole - a scrambled jet: no wreck, no blast, no credit, and its ground is free. */
export function removeProp(ctx: Ctx, id: number): void {
  const state = ctx.world.props.find((p) => p.id === id);
  if (!state || state.destroyed) return;
  state.destroyed = true;
  state.removed = true;
  ctx.staticsDirty = true;
  ctx.events.push({ type: 'propRemoved', id });
}

/**
 * `told`: whether a refusal ("NO EFFECT ...") is his to hear. It follows `byPlayer` unless the blast only
 * counts as his - a flamethrower's tank he ruptured is his kill, but not a grenade he threw at a wall (ROUND7).
 */
export function explode(
  ctx: Ctx, x: number, z: number, radius: number, power: BlastPower, byPlayer: boolean, told = byPlayer,
): void {
  const w = ctx.world;
  ctx.events.push({ type: 'explosion', x, z, radius, big: power !== 'grenade' });
  // HAZ: live mines in the blast pop, armour this close is destroyed, fuel tanks catch (hazards/blast.ts).
  blastHazards(ctx, x, z, radius, power, byPlayer);
  const lethal = radius * 0.5;
  for (const e of w.enemies) {
    if (!isAlive(e)) continue;
    const d = Math.sqrt((e.x - x) * (e.x - x) + (e.z - z) * (e.z - z));
    if (d <= lethal) damageEnemy(ctx, e, 99, byPlayer);
    else if (d <= radius) damageEnemy(ctx, e, 1, byPlayer);
  }
  const r = w.ranger;
  const dr = Math.sqrt((r.x - x) * (r.x - x) + (r.z - z) * (r.z - z));
  // A crawler in a trench is below the blast unless it lands in the trench with him.
  const sheltered = r.inTrench && r.stance === 'crawl' && dr > 2.5;
  if (!sheltered) {
    if (dr <= lethal * 0.9) woundRanger(ctx, 2, 'blast');
    else if (dr <= radius) woundRanger(ctx, 1, 'blast');
  }
  // What it does to the props around it (breach.ts): a wall only within a short reach, so a LAW opens a gap.
  let wallRefused = false;
  for (const c of ctx.statics.colliders) {
    if (!mayReach(c, x, z, radius)) continue;
    const prop = ctx.statics.def.props.find((p) => p.id === c.propId);
    const verdict = blastVerdict(prop, c, x, z, radius, power);
    if (verdict === null || !prop) continue;
    if (verdict === 'destroy') destroyProp(ctx, c.propId);
    else if (verdict === 'wrongFace') refuseBlast(ctx, prop, told);
    else if (!wallRefused) {
      // One grenade against a wall is one "NO EFFECT", however many pieces it lay beside.
      wallRefused = true;
      refuseWall(ctx, prop, told);
    }
  }
  makeNoise(ctx, x, z, power === 'grenade' ? GRENADE.noise : BOMB.noise, 'blast');
}
